// season.ts
// T-104 (v1.8.25, Kundin-Mail 23.09.2026 13:25): Floßfahrten finden nur in der Saison Mai bis Oktober statt
// ("außerhalb dieser Monate KEINE Floßfahrten"). Die Monatsnavigation der Kalender (Online-Buchung,
// Kundenkonto, Verwaltung, Buchungsablauf der Verwaltung) springt deshalb von Oktober direkt auf Mai des
// Folgejahres und von Mai zurück auf Oktober des Vorjahres. Saison bestätigt durch die Kundin 24.09.2026
// (01.05. bis 31.10.). Grenzen hier zentral; eine spätere Systemeinstellung kann sie ersetzen.

/** Erster Saisonmonat (1 = Januar). */
export const SEASON_START_MONTH = 5;
/** Letzter Saisonmonat (1 = Januar). */
export const SEASON_END_MONTH = 10;

const monthOf = (d: Date): number => d.getMonth() + 1;

/** Liegt der Monat des Datums in der Saison (Mai bis Oktober)? */
export const isSeasonMonth = (d: Date): boolean =>
  monthOf(d) >= SEASON_START_MONTH && monthOf(d) <= SEASON_END_MONTH;

/** Erster Tag des Monats (lokale Zeit). */
const firstOfMonth = (year: number, month1: number): Date => new Date(year, month1 - 1, 1);

/**
 * Nächster Saisonmonat nach dem Monat von `current`: innerhalb der Saison der Folgemonat, ab Oktober
 * (oder aus dem Winter heraus) der Mai des Folgejahres bzw. des laufenden Jahres (Januar bis April).
 */
export const nextSeasonMonth = (current: Date): Date => {
  const m = monthOf(current);
  const y = current.getFullYear();
  if (m >= SEASON_START_MONTH && m < SEASON_END_MONTH) return firstOfMonth(y, m + 1);
  if (m < SEASON_START_MONTH) return firstOfMonth(y, SEASON_START_MONTH);
  return firstOfMonth(y + 1, SEASON_START_MONTH); // Oktober bis Dezember
};

/**
 * Vorheriger Saisonmonat vor dem Monat von `current`: innerhalb der Saison der Vormonat, ab Mai
 * (oder aus dem Winter heraus) der Oktober des Vorjahres bzw. des laufenden Jahres (November, Dezember).
 */
export const prevSeasonMonth = (current: Date): Date => {
  const m = monthOf(current);
  const y = current.getFullYear();
  if (m > SEASON_START_MONTH && m <= SEASON_END_MONTH) return firstOfMonth(y, m - 1);
  if (m > SEASON_END_MONTH) return firstOfMonth(y, SEASON_END_MONTH);
  return firstOfMonth(y - 1, SEASON_END_MONTH); // Januar bis Mai
};

/**
 * Startmonat für einen Kalender: der aktuelle Monat, wenn er in der Saison liegt, sonst der nächste Mai
 * (November/Dezember: Folgejahr, Januar bis April: laufendes Jahr).
 */
export const initialSeasonMonth = (today: Date = new Date()): Date => {
  if (isSeasonMonth(today)) return firstOfMonth(today.getFullYear(), monthOf(today));
  return nextSeasonMonth(today);
};

/**
 * T-114 Punkt 3 / T-115 (v1.8.31, 27.09.2026): Startmonat des Buchungskalenders = Monat des ersten Termins ab heute
 * (freigegeben oder geplant), statt fest im laufenden Saisonmonat. Liefert den ersten Tag dieses Monats oder null,
 * wenn es keinen Termin ab heute gibt (dann bleibt es bei initialSeasonMonth). Termine vor heute zählen nicht.
 */
export const firstMonthWithSlots = (slotDates: string[], today: Date = new Date()): Date | null => {
  const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const future = slotDates
    .filter(d => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d))
    .map(d => d.substring(0, 10))
    .filter(d => d >= todayStr)
    .sort();
  if (future.length === 0) return null;
  const [y, m] = future[0].split('-').map(Number);
  return firstOfMonth(y, m);
};

/** Erster Tag des Monats eines Datums im Format YYYY-MM-DD (lokale Zeit). */
export const monthOfDateString = (date: string): Date => {
  const [y, m] = date.substring(0, 10).split('-').map(Number);
  return firstOfMonth(y, m);
};

/** Kurzer Hinweistext für Kalenderköpfe. */
export const SEASON_HINT = 'Saison Mai bis Oktober';
